import { useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, retryRead, readRetryDelay } from '../api.js';
import { useAccess } from '../access.js';
import { Priority, STATUS_LABELS, observedAt } from '../components/LeadFacts.jsx';
import {
  LeadContacts,
  LeadLocation,
  LeadWebsiteResearch,
  LeadRegistry,
} from '../components/LeadOverview.jsx';

export default function LeadDetail() {
  const { id } = useParams();
  const query = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.getLead(id),
    retry: retryRead,
    retryDelay: readRetryDelay,
  });
  if (query.isLoading) return <p className="text-slate-500">Carregando empresa…</p>;
  if (query.error)
    return (
      <div role="alert" className="space-y-4">
        <p className="text-red-600">{query.error.message}</p>
        <button className="btn-secondary" onClick={() => query.refetch()}>
          Tentar novamente
        </button>
        <Link className="ml-3 text-brand-700" to="/leads">
          Voltar à base de leads
        </Link>
      </div>
    );
  return <LeadView key={`${id}:${query.data.revision}`} lead={query.data} />;
}
function LeadView({ lead }) {
  const location = useLocation();
  const from =
    typeof location.state?.from === 'string' &&
    /^\/(?:leads(?:\?|$)|campaigns\/[a-f0-9-]+$)/.test(location.state.from)
      ? location.state.from
      : '/leads';
  const { can } = useAccess();
  const client = useQueryClient();
  const [draft, setDraft] = useState(lead.outreach_draft);
  const [status, setStatus] = useState(lead.status);
  const [copyMessage, setCopyMessage] = useState('');
  const save = useMutation({
    mutationFn: (input) => api.updateLead(lead.id, input),
    onSuccess: (row) => {
      client.setQueryData(['lead', lead.id], row);
      client.invalidateQueries({ queryKey: ['leads'] });
    },
  });
  return (
    <div className="space-y-6">
      <Link to={from} className="text-sm text-brand-700 hover:underline">
        {from.startsWith('/campaigns/') ? '← Voltar à campanha' : '← Base de leads'}
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{lead.canonical_name}</h1>
          <p className="text-slate-500 mt-1">
            {lead.address || lead.region || 'Endereço não registrado'}
          </p>
        </div>
        <Priority lead={lead} />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Fact
          title="Origem dos dados"
          value={
            lead.provider === 'google_places'
              ? 'Google Maps · Places'
              : lead.provider || 'Não informada'
          }
          detail={`Consultado em ${observedAt(lead.observed_at)}`}
        />
        <Fact
          title="Avaliação por IA"
          value={lead.score === null ? 'Ainda não avaliado' : `${lead.score}/100 de aderência`}
          detail={lead.model_key ? `Modelo: ${lead.model_key}` : 'Modelo não registrado'}
        />
        <Fact
          title="Campanha de origem"
          value={lead.discovery_campaign_name || 'Não registrada'}
          detail={lead.region ? `Região solicitada: ${lead.region}` : ''}
        />
      </div>
      {lead.discovery_campaign_id && (
        <Link
          to={`/campaigns/${lead.discovery_campaign_id}`}
          className="inline-block text-sm text-brand-700 hover:underline"
        >
          Ver campanha de origem →
        </Link>
      )}
      {lead.isDemo && (
        <p className="rounded-lg bg-amber-50 p-4 text-amber-800 text-sm">
          Registro de demonstração criado anteriormente. Não representa uma empresa verificada e não
          deve ser usado para prospecção.
        </p>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <LeadContacts lead={lead} />
        <LeadLocation lead={lead} />
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <h2 className="font-semibold">Análise da IA e evidências</h2>
          <p>{lead.priorityReason}</p>
          <p className="text-sm text-slate-500">
            {lead.confidence !== null
              ? `Confiança da avaliação: ${Math.round(lead.confidence * 100)}%.`
              : 'Ainda não há avaliação.'}{' '}
            {lead.assessed_at && `Avaliado em ${observedAt(lead.assessed_at)}.`}
          </p>
          {lead.intent_text && (
            <p className="text-sm">
              <strong>Objetivo da campanha:</strong> {lead.intent_text}
            </p>
          )}
          {[
            ['positiveSignals', 'Sinais favoráveis'],
            ['negativeSignals', 'Pontos a conferir'],
            ['evidence', 'Evidências recebidas'],
          ].map(
            ([key, title]) =>
              lead.intelligence?.[key]?.length > 0 && (
                <div key={key}>
                  <h3 className="text-sm font-medium">{title}</h3>
                  <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                    {lead.intelligence[key].map((signal, index) => (
                      <li key={index}>
                        {signal.signal}
                        <span className="block text-xs text-slate-500">{signal.quote}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
          )}
          <p className="text-xs text-slate-500">
            Aderência é uma estimativa para ajudar a revisar a lista. Necessidades comerciais devem
            ser confirmadas em conversa.
          </p>
        </section>
        <div className="space-y-6">
          <LeadWebsiteResearch lead={lead} />
          <LeadRegistry lead={lead} />
        </div>
      </div>
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-semibold">Abordagem comercial</h2>
        <p className="text-sm text-slate-500">
          Personalize o texto antes de usar. O rascunho fica privado neste tenant.
        </p>
        <label className="block text-sm space-y-1 max-w-xs">
          <span>Status do lead</span>
          <select
            className="input"
            disabled={!can('leads.write') || save.isPending}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm space-y-1">
          <span>Rascunho de abordagem</span>
          <textarea
            rows="6"
            className="input"
            maxLength={5000}
            readOnly={!can('leads.write')}
            value={draft}
            placeholder="Use o modelo inicial ou escreva sua abordagem."
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap gap-3">
          {can('leads.write') && (
            <>
              <button
                className="btn-secondary"
                disabled={Boolean(draft) || save.isPending}
                onClick={() => setDraft(lead.outreachSuggestion)}
              >
                Usar modelo inicial
              </button>
              <button
                className="btn-primary"
                disabled={
                  save.isPending || (draft === lead.outreach_draft && status === lead.status)
                }
                onClick={() =>
                  save.mutate({
                    requestId: crypto.randomUUID(),
                    revision: lead.revision,
                    outreachDraft: draft,
                    status,
                  })
                }
              >
                {save.isPending ? 'Salvando…' : 'Salvar rascunho e status'}
              </button>
            </>
          )}
          <button
            className="btn-secondary"
            disabled={!draft}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(draft);
                setCopyMessage('Texto copiado.');
              } catch {
                setCopyMessage('Selecione o texto para copiar.');
              }
            }}
          >
            Copiar texto
          </button>
        </div>
        {copyMessage && (
          <p role="status" className="text-sm text-slate-600">
            {copyMessage}
          </p>
        )}
        {save.error && (
          <p role="alert" className="text-sm text-red-600">
            {save.error.message}
          </p>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-semibold">Fontes e histórico de consulta</h2>
        {lead.sources?.length ? (
          <ul className="divide-y divide-slate-100">
            {lead.sources.map((source) => (
              <li key={source.id} className="py-3 text-sm space-y-1">
                <p className="font-medium">
                  {source.provider === 'google_places'
                    ? 'Google Maps · Places'
                    : source.provider || 'Fonte não registrada'}{' '}
                  · {observedAt(source.observed_at)}
                </p>
                <p className="text-slate-600">
                  {source.campaign_name} · {source.keyword} · {source.location_text}
                </p>
                {source.url ? (
                  <a
                    className="text-brand-700 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                    href={source.url}
                  >
                    Abrir fonte registrada
                  </a>
                ) : (
                  <p className="text-slate-500">Link da fonte não registrado.</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Não há observações com fonte nesta base.</p>
        )}
        <p className="text-xs text-slate-500">
          Até 30 observações mais recentes. Os dados representam a data da consulta e podem ter
          mudado.
        </p>
      </section>
    </div>
  );
}

function Fact({ title, value, detail }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
      <p className="font-semibold text-sm">{value}</p>
      {detail && <p className="text-xs text-slate-500">{detail}</p>}
    </div>
  );
}
